import { ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from 'reactstrap';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import humanize from 'humanize-plus';
import PropTypes from 'prop-types';
import entityComposition from '../common/classes/entityComposition';
import DocumentAssociations from './documentAssociations';
import Icon from '../common/containers/icon';
const DocumentsBulkActions = props => {
  const {
    document,
    documentActions,
    jobBulkActionDocumentActions,
    jobIndexQuery,
    documentTagActions,
    documentTagIndexQuery,
    notificationActions,
    jobs,
    documentTags
  } = props;
  const currentActionRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [actionTrigger, setActionTrigger] = useState(0);
  const handleDelete = useCallback(ids => {
    // Figure out if we need to show the delete confirmation message
    const jobAssociations = (ids || []).reduce((memo, id) => {
      const doc = document.selections.find(d => d.id === id);
      if (doc && doc.jobs && doc.jobs.length) {
        const jobNumbers = doc.jobs.map(next => next.jobNumber);
        jobNumbers.forEach(job => {
          if (memo.indexOf(job) === -1) {
            memo.push(job);
          }
        });
      }
      return memo;
    }, []);
    if (jobAssociations.length) {
      const oneDoc = ids.length === 1;
      const jobsString = jobAssociations.length === 1 ? 'one job:' : 'these jobs:';
      const header = `The selected document${oneDoc ? ' is' : 's are'} associated with ${jobsString}`;
      const footer = `Are you sure you want to delete the selected document${oneDoc ? '' : 's'}?`;
      const message = `\n${humanize.oxford(jobAssociations)}\n\n${footer}`;
      notificationActions.info({
        headerMessage: header,
        message,
        rightButtonLabel: 'Don\'t delete',
        leftButtonLabel: 'Delete',
        leftButtonAction: () => {
          documentActions.serverDestroy(ids);
          documentActions.selectClearAll();
        },
        id: 'delete_doc_confirm'
      }, {
        autoClose: false
      }); // need user feedback here
    } else {
      // No documents are linked to jobs, so just delete
      documentActions.serverDestroy(ids);
      documentActions.selectClearAll();
    }
  }, [document.selections, documentActions, notificationActions]);

  // Process actions when they're set and component has updated
  useEffect(() => {
    if (currentActionRef.current && document.selections.length > 0) {
      const action = currentActionRef.current;
      currentActionRef.current = null;
      switch (action) {
        case 'delete':
          handleDelete(document.selected);
          break;
        case 'edit':
          documentActions.setIsSelectingAssociations(true);
          break;
        default:
          break;
      }
    }
  }, [actionTrigger, document.selections.length, document.selected, documentActions, handleDelete]);
  const handleAction = useCallback(action => {
    documentActions.serverIndexDetail(document.selected);
    if (action === 'edit') {
      jobBulkActionDocumentActions.serverIndex(jobIndexQuery);
      documentTagActions.serverIndex(documentTagIndexQuery);
    }
    currentActionRef.current = action;
    setActionTrigger(prev => prev + 1);
  }, [documentActions, document.selected, jobBulkActionDocumentActions, jobIndexQuery, documentTagActions, documentTagIndexQuery]);
  const toggle = useCallback(() => {
    setDropdownOpen(prev => !prev);
  }, []);

  // simplify doc store names
  const {
    selected,
    selectingAssociations
  } = document;
  return /*#__PURE__*/React.createElement(ButtonDropdown, {
    isOpen: dropdownOpen,
    toggle: toggle,
    id: "assetActions",
    className: "t-dba-action-dropdown",
    size: "sm"
  }, /*#__PURE__*/React.createElement(DropdownToggle, {
    disabled: selected.length === 0,
    caret: true
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Icon, {
    glyph: "bolt"
  }), " Actions")), /*#__PURE__*/React.createElement(DropdownMenu, null, /*#__PURE__*/React.createElement(DropdownItem, {
    className: "t-dba-delete",
    onClick: () => handleAction('delete')
  }, "Delete"), /*#__PURE__*/React.createElement(DropdownItem, {
    className: "t-dba-edit-association",
    onClick: () => handleAction('edit')
  }, "Edit Associations"), selectingAssociations ? /*#__PURE__*/React.createElement(DocumentAssociations, {
    document: document,
    documentActions: documentActions,
    jobOptions: jobs // all jobs minus closed ones
    ,
    tagOptions: documentTags
  }) : null));
};
DocumentsBulkActions.propTypes = {
  document: PropTypes.shape({
    selections: PropTypes.arrayOf(PropTypes.shape({})),
    selected: PropTypes.arrayOf(PropTypes.string),
    selectingAssociations: PropTypes.bool
  }).isRequired,
  documentActions: PropTypes.shape({
    selectClearAll: PropTypes.func,
    serverDestroy: PropTypes.func,
    serverIndex: PropTypes.func,
    setIsSelectingAssociations: PropTypes.func,
    serverIndexDetail: PropTypes.func
  }).isRequired,
  documentTagActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverIndexDetail: PropTypes.func
  }).isRequired,
  jobBulkActionDocumentActions: PropTypes.shape({
    serverIndex: PropTypes.func,
    serverIndexDetail: PropTypes.func
  }).isRequired,
  jobs: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  jobIndexQuery: PropTypes.shape({}).isRequired,
  documentTags: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  documentTagIndexQuery: PropTypes.shape({}).isRequired,
  notificationActions: PropTypes.shape({
    info: PropTypes.func
  }).isRequired
};
export { DocumentsBulkActions as TestDocumentsBulkActions };
export default entityComposition(DocumentsBulkActions, ['document'], {
  jobs: state => state.jobBulkActionDocument.list,
  jobIndexQuery: state => state.jobBulkActionDocument.indexQuery,
  documentTags: state => state.documentTag.list,
  documentTagIndexQuery: state => state.documentTag.indexQuery
}, ['jobBulkActionDocument', 'documentTag', 'notification']);